import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  Calendar,
  CircleCheck,
  FilePlus2,
  FileText,
  Landmark,
  Layers,
  Lightbulb,
  Network,
  Paperclip,
  Plus,
  Repeat,
  Save,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  PAYMENT_TERM_PRESETS,
  formatCents,
  type ChartAccount,
  type FinancialEntry,
  type FinancialEntryType,
} from '@amai/contracts';
import { Button } from '@/components/Button';
import { CollapsibleSection } from '@/components/FormSection';
import { MoneyInput } from '@/components/MoneyInput';
import { api, upload } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import {
  useBusinesses,
  useChartAccountGroups,
  useCustomers,
  useCostCenters,
  useSuppliers,
  todayISO,
} from '@/lib/queries';
import { DateInput } from '@/components/DateInput';

interface InstallmentDraft {
  dueDate: string;
  amountCents: number | null;
}

/** Vencimento em N meses a partir de hoje, preservando o dia quando possível. */
function addMonths(iso: string, months: number): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  // Campo de data limpo no navegador chega como '' — devolver a própria
  // entrada evita o RangeError do toISOString derrubar a tela inteira.
  if (Number.isNaN(date.getTime())) return iso;
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, lastDay));
  return date.toISOString().slice(0, 10);
}

/** Vencimento em N dias corridos — usado pelas condições de pagamento. */
function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return iso;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Colunas da parcela. Uma constante para cabeçalho e linha não divergirem. */
const COLUNAS_DA_PARCELA = '72px minmax(0, 1fr) 176px 40px';

/** O mesmo teto da API (UPLOAD_MAX_MB). Avisar antes poupa o operador de esperar o upload falhar. */
const ANEXO_MAX_MB = 10;
const ANEXO_ACEITOS = '.pdf,.jpg,.jpeg,.png,.webp,.xml';
const OBSERVACOES_MAX = 500;

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/**
 * Sugestões rápidas: as contas que todo mês chegam. Cada uma aponta para uma
 * conta REAL do plano de contas — se o plano do parque não tiver nada que
 * case, a sugestão nem aparece. Sugestão que preenche conta errada é pior
 * que sugestão nenhuma.
 */
type Sugestao = { titulo: string; descricao: string; casa: (conta: ChartAccount) => boolean };

/** As receitas que mais se lançam à mão — evento, excursão, área VIP. */
const SUGESTOES_RECEBER: Sugestao[] = [
  {
    titulo: 'Evento / aniversário',
    descricao: 'Evento',
    casa: (conta) => /evento|anivers/i.test(conta.name),
  },
  {
    titulo: 'Excursão / escola',
    descricao: 'Excursão',
    casa: (conta) => /excurs|escola/i.test(conta.name),
  },
  {
    titulo: 'Área VIP / bangalô',
    descricao: 'Locação de área VIP',
    casa: (conta) => /vip|bangal/i.test(conta.name),
  },
  {
    titulo: 'Patrocínio',
    descricao: 'Patrocínio',
    casa: (conta) => /patroc/i.test(conta.name),
  },
];

const SUGESTOES: Sugestao[] = [
  {
    titulo: 'Conta de energia',
    descricao: 'Conta de energia',
    casa: (conta) => /energia/i.test(conta.name),
  },
  {
    titulo: 'Conta de água',
    descricao: 'Conta de água',
    casa: (conta) => /água/i.test(conta.name) && !/tratamento/i.test(conta.name),
  },
  {
    titulo: 'Internet e telefone',
    descricao: 'Internet e telefone',
    casa: (conta) => /internet|telefon/i.test(conta.name),
  },
  {
    titulo: 'Manutenção',
    descricao: 'Manutenção',
    casa: (conta) => /^manuten/i.test(conta.name),
  },
];

/** Campo com ícone à esquerda, como no resto do formulário. */
function ComIcone({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="lanc__comicone">
      <Icon size={16} aria-hidden className="lanc__icone" />
      {children}
    </div>
  );
}

function Campo({
  label,
  obrigatorio,
  error,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  obrigatorio?: boolean;
  error?: string;
  hint?: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="field lanc__campo">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
        {obrigatorio && (
          <span className="lanc__obrigatorio" aria-hidden>
            {' '}
            *
          </span>
        )}
      </label>
      {children}
      {error ? (
        <div className="field__error" role="alert">
          {error}
        </div>
      ) : (
        hint && <div className="field__hint">{hint}</div>
      )}
    </div>
  );
}

/**
 * Lançamento manual — conta a pagar ou a receber.
 *
 * Modal em duas colunas: à esquerda o que a API exige (descrição, plano de
 * contas, valor, vencimento, centro de custo), à direita os atalhos —
 * sugestões que preenchem descrição + conta, e os anexos.
 *
 * Os anexos só sobem DEPOIS que o lançamento existe (a API anexa a um
 * registro). Se um falhar, o lançamento já foi criado: a tela diz isso e
 * troca o botão de salvar por "Fechar" — salvar de novo duplicaria a conta.
 */
export function EntryFormDrawer({
  open,
  type,
  canAttach,
  onClose,
  onCreated,
}: {
  open: boolean;
  type: FinancialEntryType;
  /** Anexar pede `lancamentos:editar` na API; sem ela a área nem aparece. */
  canAttach: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const costCenters = useCostCenters();
  const businesses = useBusinesses();
  const planoDeContas = useChartAccountGroups(type);
  const suppliers = useSuppliers();
  const customers = useCustomers();
  const fileInput = useRef<HTMLInputElement>(null);

  const [description, setDescription] = useState('');
  const [personId, setPersonId] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [chartAccountId, setChartAccountId] = useState('');
  const [issueDate, setIssueDate] = useState(todayISO());
  const [competenceDate, setCompetenceDate] = useState(todayISO());
  const [documentNumber, setDocumentNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [totalCents, setTotalCents] = useState<number | null>(null);
  const [term, setTerm] = useState('');
  const [installmentCount, setInstallmentCount] = useState(1);
  const [firstDueDate, setFirstDueDate] = useState(addMonths(todayISO(), 1));
  const [installments, setInstallments] = useState<InstallmentDraft[] | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [lastOpen, setLastOpen] = useState(open);

  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setDescription('');
      setPersonId('');
      setBusinessId('');
      setCostCenterId('');
      setChartAccountId('');
      setIssueDate(todayISO());
      setCompetenceDate(todayISO());
      setDocumentNumber('');
      setNotes('');
      setTotalCents(null);
      setTerm('');
      setInstallmentCount(1);
      setFirstDueDate(addMonths(todayISO(), 1));
      setInstallments(null);
      setFiles([]);
      setFileError(null);
    }
  }

  const mutation = useMutation({
    mutationFn: async (body: unknown) => {
      const created = await api.post<FinancialEntry>('/financial-entries', body);
      const falharam: string[] = [];
      for (const file of files) {
        const form = new FormData();
        form.set('entity', 'FinancialEntry');
        form.set('entityId', created.id);
        form.set('file', file);
        try {
          await upload('/attachments', form);
        } catch (error) {
          falharam.push(`${file.name}: ${errorMessage(error)}`);
        }
      }
      return { code: created.code, falharam };
    },
    onSuccess: (result) => {
      if (result.falharam.length === 0) onCreated();
    },
  });

  // Reabrir começa do zero — inclusive o aviso de anexo que falhou da vez anterior.
  const { reset } = mutation;
  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  // Criado, mas algum anexo não subiu: o lançamento EXISTE. Daqui só se fecha.
  const criadoComFalha = mutation.data && mutation.data.falharam.length > 0 ? mutation.data : null;

  const errors = fieldErrors(mutation.error);

  /**
   * Divide o total em N parcelas. A diferença de arredondamento vai para a
   * PRIMEIRA parcela — assim a soma fecha exatamente com o total, sempre.
   */
  const buildInstallments = (): InstallmentDraft[] => {
    const total = totalCents ?? 0;
    if (total <= 0) return [];

    // Condição de pagamento escolhida: dias corridos a partir da EMISSÃO.
    const preset = PAYMENT_TERM_PRESETS.find((item) => item.key === term);
    if (preset) {
      const count = preset.days.length;
      const base = Math.floor(total / count);
      const remainder = total - base * count;
      return preset.days.map((days, index) => ({
        dueDate: addDays(issueDate, days),
        amountCents: index === 0 ? base + remainder : base,
      }));
    }

    if (installmentCount < 1) return [];
    const base = Math.floor(total / installmentCount);
    const remainder = total - base * installmentCount;
    return Array.from({ length: installmentCount }, (_, index) => ({
      dueDate: addMonths(firstDueDate, index),
      amountCents: index === 0 ? base + remainder : base,
    }));
  };

  const draft = installments ?? buildInstallments();
  const sum = draft.reduce((acc, item) => acc + (item.amountCents ?? 0), 0);
  const balanced = sum === (totalCents ?? 0) && sum > 0;

  // Com uma empresa só não há o que escolher: ela vem marcada. Com duas ou
  // mais, a escolha é do operador — chutar a empresa de uma conta é como a
  // DRE de uma passa a carregar o custo da outra.
  const empresas = businesses.data ?? [];
  const empresa = businessId || (empresas.length === 1 ? empresas[0]!.id : '');

  const canSubmit = description.trim().length >= 2 && empresa && chartAccountId && balanced;

  const isPayable = type === 'PAYABLE';
  const title = isPayable ? 'Nova conta a pagar' : 'Nova conta a receber';
  // A tabela de parcelas só aparece quando há o que conferir: com parcela
  // única e sem condição, o "Vencimento" lá em cima já diz tudo.
  const mostrarParcelas = draft.length > 1 || Boolean(term) || installments !== null;

  const contas = planoDeContas.data?.contas ?? [];
  const grupos = planoDeContas.data?.grupos ?? [];
  const pessoas = (isPayable ? suppliers.data : customers.data) ?? [];
  const sugestoes = (isPayable ? SUGESTOES : SUGESTOES_RECEBER).flatMap((sugestao) => {
    const conta = contas.find(sugestao.casa);
    return conta ? [{ ...sugestao, conta }] : [];
  });

  const hoje = new Date(`${todayISO()}T12:00:00`);
  const mesAno = `${MESES[hoje.getMonth()]}/${hoje.getFullYear()}`;

  const adicionarArquivos = (lista: FileList | null) => {
    if (!lista) return;
    const grandes = Array.from(lista).filter((file) => file.size > ANEXO_MAX_MB * 1024 * 1024);
    setFileError(
      grandes.length > 0
        ? `${grandes.map((file) => file.name).join(', ')} passa de ${ANEXO_MAX_MB} MB. Reduza a imagem ou mande o PDF compactado.`
        : null,
    );
    const cabem = Array.from(lista).filter((file) => file.size <= ANEXO_MAX_MB * 1024 * 1024);
    setFiles((atuais) => [...atuais, ...cabem]);
  };

  const fechar = criadoComFalha ? onCreated : onClose;

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !mutation.isPending) fechar();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, fechar, mutation.isPending]);

  if (!open) return null;

  return (
    <>
      <div
        className="overlay overlay--modal"
        onClick={mutation.isPending ? undefined : fechar}
        aria-hidden
      />
      <div className="modal lanc" role="dialog" aria-modal="true" aria-label={title}>
        <header className="lanc__head">
          <span className="lanc__selo" aria-hidden>
            <FilePlus2 size={24} />
          </span>
          <div className="lanc__cabeca">
            <h2 className="lanc__titulo">{title}</h2>
            <p className="lanc__subtitulo">
              Cadastre um lançamento financeiro com mais rapidez e organização.
            </p>
          </div>
          <button
            type="button"
            className="ctxpanel__close"
            onClick={fechar}
            disabled={mutation.isPending}
            aria-label="Fechar"
          >
            <X size={18} aria-hidden />
          </button>
        </header>

        {criadoComFalha ? (
          <div className="lanc__aviso" role="alert">
            <CircleCheck size={18} aria-hidden className="lanc__aviso-ok" />
            <div>
              <strong>Lançamento {criadoComFalha.code} criado.</strong> Mas{' '}
              {criadoComFalha.falharam.length === 1
                ? 'um anexo não subiu'
                : 'alguns anexos não subiram'}
              :
              <ul>
                {criadoComFalha.falharam.map((linha) => (
                  <li key={linha}>{linha}</li>
                ))}
              </ul>
              Abra o lançamento na lista e anexe de novo por lá — não lance outra vez, ou a conta
              fica em dobro.
            </div>
          </div>
        ) : (
          <>
            {mutation.error && (
              <div className="login__alert" role="alert" style={{ marginBottom: 16 }}>
                <AlertTriangle size={15} aria-hidden style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{errorMessage(mutation.error)}</span>
              </div>
            )}

            <div className="lanc__corpo">
              <div className="lanc__form">
                <Campo label="Empresa" obrigatorio error={errors.businessId} htmlFor="entry-business">
                  <ComIcone icon={Landmark}>
                    <select
                      id="entry-business"
                      className="input"
                      value={empresa}
                      onChange={(event) => setBusinessId(event.target.value)}
                    >
                      <option value="">Selecione a empresa</option>
                      {empresas.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </ComIcone>
                </Campo>

                <Campo
                  label="Descrição"
                  obrigatorio
                  error={errors.description}
                  htmlFor="entry-description"
                >
                  <ComIcone icon={FileText}>
                    <input
                      id="entry-description"
                      className="input"
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      placeholder={
                        isPayable
                          ? `Ex.: Conta de luz ${mesAno}`
                          : 'Ex.: Evento de aniversário — turma 5ºB'
                      }
                    />
                  </ComIcone>
                </Campo>

                <Campo
                  label="Plano de contas"
                  obrigatorio
                  error={errors.chartAccountId}
                  htmlFor="entry-account"
                >
                  <ComIcone icon={Layers}>
                    <select
                      id="entry-account"
                      className="input"
                      value={chartAccountId}
                      onChange={(event) => setChartAccountId(event.target.value)}
                    >
                      <option value="">Selecione a categoria</option>
                      {/* Agrupado como na tela de Plano de contas. */}
                      {grupos.map((grupo) => (
                        <optgroup key={grupo.label} label={grupo.label}>
                          {grupo.accounts.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.code} · {account.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </ComIcone>
                </Campo>

                <div className="lanc__dupla">
                  <Campo
                    label={isPayable ? 'Fornecedor' : 'Cliente'}
                    error={errors.personId}
                    htmlFor="entry-person"
                  >
                    <ComIcone icon={Building2}>
                      <select
                        id="entry-person"
                        className="input"
                        value={personId}
                        onChange={(event) => setPersonId(event.target.value)}
                      >
                        <option value="">
                          {isPayable ? 'Selecionar fornecedor' : 'Selecionar cliente'}
                        </option>
                        {pessoas.map((person) => (
                          <option key={person.id} value={person.id}>
                            {person.tradeName || person.name}
                          </option>
                        ))}
                      </select>
                    </ComIcone>
                  </Campo>

                  <Campo label="Nº documento" error={errors.documentNumber} htmlFor="entry-doc">
                    <ComIcone icon={FileText}>
                      <input
                        id="entry-doc"
                        className="input"
                        value={documentNumber}
                        onChange={(event) => setDocumentNumber(event.target.value)}
                        placeholder="NF, boleto etc."
                      />
                    </ComIcone>
                  </Campo>
                </div>

                <div className="lanc__dupla">
                  <Campo label="Valor" obrigatorio htmlFor="entry-total">
                    {/* O MoneyInput já traz o "R$" à esquerda — ele faz o papel do ícone. */}
                    <MoneyInput
                      id="entry-total"
                      value={totalCents}
                      onChange={(cents) => {
                        setTotalCents(cents);
                        setInstallments(null);
                      }}
                    />
                  </Campo>

                  <Campo
                    label={draft.length > 1 ? 'Primeiro vencimento' : 'Vencimento'}
                    obrigatorio
                    hint={term ? 'Calculado pela condição de pagamento' : undefined}
                    htmlFor="entry-first-due"
                  >
                    <ComIcone icon={Calendar}>
                      <DateInput
                        id="entry-first-due"
                        className="input"
                        value={term ? (draft[0]?.dueDate ?? firstDueDate) : firstDueDate}
                        disabled={Boolean(term)}
                        onChange={(iso) => {
                          setFirstDueDate(iso);
                          setInstallments(null);
                        }}
                      />
                    </ComIcone>
                  </Campo>
                </div>

                <div className="lanc__dupla">
                  <Campo label="Parcelas" htmlFor="entry-count">
                    <ComIcone icon={Repeat}>
                      <select
                        id="entry-count"
                        className="input"
                        disabled={Boolean(term)}
                        value={installmentCount}
                        onChange={(event) => {
                          setInstallmentCount(Number(event.target.value));
                          setInstallments(null);
                        }}
                      >
                        <option value={1}>Única</option>
                        {Array.from({ length: 35 }, (_, index) => index + 2).map((n) => (
                          <option key={n} value={n}>
                            {n}x mensais
                          </option>
                        ))}
                      </select>
                    </ComIcone>
                  </Campo>

                  <Campo
                    label="Centro de custo"
                    error={errors.costCenterId}
                    hint="Opcional: detalha o custo dentro da empresa"
                    htmlFor="entry-cost-center"
                  >
                    <ComIcone icon={Network}>
                      <select
                        id="entry-cost-center"
                        className="input"
                        value={costCenterId}
                        onChange={(event) => setCostCenterId(event.target.value)}
                      >
                        <option value="">Nenhum</option>
                        {(costCenters.data ?? []).map((center) => (
                          <option key={center.id} value={center.id}>
                            {center.code} · {center.name}
                          </option>
                        ))}
                      </select>
                    </ComIcone>
                  </Campo>
                </div>

                <Campo
                  label="Condição de pagamento"
                  hint={term ? 'Dias contados da data de emissão' : undefined}
                  htmlFor="entry-term"
                >
                  <ComIcone icon={Calendar}>
                    <select
                      id="entry-term"
                      className="input"
                      value={term}
                      onChange={(event) => {
                        setTerm(event.target.value);
                        setInstallments(null);
                      }}
                    >
                      <option value="">Selecione a condição</option>
                      {PAYMENT_TERM_PRESETS.map((preset) => (
                        <option key={preset.key} value={preset.key}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </ComIcone>
                </Campo>

                {/*
                  As parcelas que VÃO NASCER, antes de nascerem. EDITÁVEIS de
                  propósito: o boleto do fornecedor raramente cai em datas
                  redondas, e obrigar o operador a aceitar "30/60/90" faria a
                  agenda de vencimentos mentir desde o primeiro dia.
                */}
                {mostrarParcelas && draft.length > 0 && (
                  <div className="linhas lanc__parcelas">
                    <div
                      className="linhas__cab"
                      style={{ gridTemplateColumns: COLUNAS_DA_PARCELA }}
                    >
                      <span>Parcela</span>
                      <span>Vencimento</span>
                      <span className="linhas__num">Valor</span>
                      <span />
                    </div>

                    {draft.map((installment, index) => (
                      <div
                        key={index}
                        className="linhas__linha"
                        style={{ gridTemplateColumns: COLUNAS_DA_PARCELA }}
                      >
                        <span
                          className="mono"
                          style={{ fontSize: 12.5, color: 'var(--ink-faint)' }}
                        >
                          {index + 1}/{draft.length}
                        </span>
                        <div className="linhas__celula" data-rotulo="Vencimento">
                          <DateInput
                            className="input input--mono"
                            value={installment.dueDate}
                            aria-label={`Vencimento da parcela ${index + 1}`}
                            onChange={(iso) => {
                              const next = [...draft];
                              next[index] = { ...installment, dueDate: iso };
                              setInstallments(next);
                            }}
                          />
                        </div>
                        <div className="linhas__celula" data-rotulo="Valor">
                          <MoneyInput
                            value={installment.amountCents}
                            ariaLabel={`Valor da parcela ${index + 1}`}
                            onChange={(cents) => {
                              const next = [...draft];
                              next[index] = { ...installment, amountCents: cents };
                              setInstallments(next);
                            }}
                          />
                        </div>
                        <div className="linhas__acao">
                          {draft.length > 1 && (
                            <Button
                              small
                              variant="ghost"
                              icon={Trash2}
                              aria-label={`Remover a parcela ${index + 1}`}
                              onClick={() => setInstallments(draft.filter((_, i) => i !== index))}
                            />
                          )}
                        </div>
                      </div>
                    ))}

                    <div className="linhas__rodape" style={{ justifyContent: 'space-between' }}>
                      <Button
                        small
                        variant="ghost"
                        icon={Plus}
                        onClick={() =>
                          setInstallments([
                            ...draft,
                            { dueDate: addMonths(firstDueDate, draft.length), amountCents: null },
                          ])
                        }
                      >
                        Adicionar parcela
                      </Button>
                      {/* A soma em vermelho é o aviso ANTES do erro do servidor. */}
                      <span
                        className="mono"
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: balanced ? 'var(--success-text)' : 'var(--danger-text)',
                        }}
                      >
                        {formatCents(sum)} de {formatCents(totalCents ?? 0)}
                      </span>
                    </div>

                    {!balanced && sum > 0 && (
                      <p className="field__error" role="alert" style={{ margin: '8px 12px 12px' }}>
                        A soma das parcelas precisa fechar com o valor total.
                      </p>
                    )}
                  </div>
                )}

                <Campo label="Observações" htmlFor="entry-notes">
                  <textarea
                    id="entry-notes"
                    className="input lanc__texto"
                    rows={3}
                    maxLength={OBSERVACOES_MAX}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="Detalhes do pagamento, número de contrato, referência interna…"
                  />
                  <div className="lanc__contador">
                    {notes.length}/{OBSERVACOES_MAX}
                  </div>
                </Campo>

                <CollapsibleSection label="Datas de emissão e competência">
                  <div className="lanc__dupla">
                    <Campo label="Data de emissão" htmlFor="entry-issue">
                      <DateInput
                        id="entry-issue"
                        className="input"
                        value={issueDate}
                        onChange={(iso) => setIssueDate(iso)}
                      />
                    </Campo>
                    <Campo
                      label="Competência"
                      hint="O mês a que este valor pertence"
                      htmlFor="entry-competence"
                    >
                      <DateInput
                        id="entry-competence"
                        className="input"
                        value={competenceDate}
                        onChange={(iso) => setCompetenceDate(iso)}
                      />
                    </Campo>
                  </div>
                </CollapsibleSection>
              </div>

              <aside className="lanc__lado">
                {sugestoes.length > 0 && (
                  <section className="lanc__sugestoes" aria-label="Sugestões rápidas">
                    <div className="lanc__sugestoes-cab">
                      <span className="lanc__lampada" aria-hidden>
                        <Lightbulb size={18} />
                      </span>
                      <div>
                        <div className="lanc__sugestoes-titulo">Sugestões rápidas</div>
                        <div className="lanc__sugestoes-texto">
                          Agilize o preenchimento com informações comuns.
                        </div>
                      </div>
                    </div>
                    {sugestoes.map((sugestao) => (
                      <button
                        key={sugestao.titulo}
                        type="button"
                        className="lanc__sugestao"
                        onClick={() => {
                          setDescription(`${sugestao.descricao} ${mesAno}`);
                          setChartAccountId(sugestao.conta.id);
                        }}
                      >
                        <span>
                          <span className="lanc__sugestao-nome">{sugestao.titulo}</span>
                          <span className="lanc__sugestao-conta">
                            Plano de contas: {sugestao.conta.name}
                          </span>
                        </span>
                        <Plus size={16} aria-hidden />
                      </button>
                    ))}
                  </section>
                )}

                {canAttach && (
                  <section className="lanc__anexos" aria-label="Anexar documentos">
                    <Paperclip size={22} aria-hidden className="lanc__clipe" />
                    <div className="lanc__anexos-titulo">
                      Anexar documentos <span>(opcional)</span>
                    </div>
                    <div className="lanc__anexos-texto">Boleto, nota fiscal, contrato etc.</div>
                    <input
                      ref={fileInput}
                      type="file"
                      multiple
                      accept={ANEXO_ACEITOS}
                      hidden
                      aria-label="Arquivos para anexar"
                      onChange={(event) => {
                        adicionarArquivos(event.target.files);
                        event.target.value = '';
                      }}
                    />
                    <Button
                      variant="outline"
                      icon={Upload}
                      onClick={() => fileInput.current?.click()}
                    >
                      Selecionar arquivos
                    </Button>
                    <div className="lanc__anexos-formatos">
                      Formatos: PDF, JPG, PNG, WEBP, XML (máx. {ANEXO_MAX_MB} MB)
                    </div>
                    {fileError && (
                      <div className="field__error" role="alert">
                        {fileError}
                      </div>
                    )}
                    {files.length > 0 && (
                      <ul className="lanc__arquivos">
                        {files.map((file, index) => (
                          <li key={`${file.name}-${index}`}>
                            <FileText size={14} aria-hidden />
                            <span className="lanc__arquivo-nome">{file.name}</span>
                            <button
                              type="button"
                              aria-label={`Tirar ${file.name}`}
                              onClick={() => setFiles(files.filter((_, i) => i !== index))}
                            >
                              <X size={14} aria-hidden />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                )}
              </aside>
            </div>
          </>
        )}

        <footer className="lanc__rodape">
          {criadoComFalha ? (
            <Button variant="primary" onClick={onCreated}>
              Fechar
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                icon={Save}
                disabled={!canSubmit}
                loading={mutation.isPending}
                onClick={() =>
                  mutation.mutate({
                    type,
                    description: description.trim(),
                    personId: personId || null,
                    businessId: empresa,
                    costCenterId: costCenterId || null,
                    chartAccountId,
                    issueDate,
                    competenceDate,
                    documentNumber: documentNumber.trim() || null,
                    notes: notes.trim() || null,
                    installments: draft.map((item) => ({
                      dueDate: item.dueDate,
                      amountCents: item.amountCents ?? 0,
                    })),
                  })
                }
              >
                Salvar lançamento
              </Button>
            </>
          )}
        </footer>
      </div>
    </>
  );
}
